// src/contexts/LocalizationContext.tsx
import { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import i18n from 'i18next';

type Language = 'ar' | 'en';

interface LocalizationContextType {
    language: Language;
    toggleLanguage: () => void;
    getLocalizedContent: (content: LocalizedContent | undefined) => string;
}

interface LocalizedContent {
    ar?: string;
    en?: string;
}

const LocalizationContext = createContext<LocalizationContextType | undefined>(undefined);

export const LocalizationProvider = ({ children }: { children: ReactNode }) => {
    const [language, setLanguage] = useState<Language>(() => {
        const savedLang = localStorage.getItem('lang') as Language;
        return savedLang || 'ar';
    });

    useEffect(() => {
        localStorage.setItem('lang', language);
        i18n.changeLanguage(language);
        document.documentElement.lang = language;
        document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    }, [language]);

    const toggleLanguage = () => {
        setLanguage((prev) => (prev === 'en' ? 'ar' : 'en'));
    };

    const getLocalizedContent = (content: LocalizedContent | undefined) => {
        if (!content) return '';
        return content[language] || content.en || content.ar || '';
    };

    return (
        <LocalizationContext.Provider value={{ language, toggleLanguage, getLocalizedContent }}>
            {children}
        </LocalizationContext.Provider>
    );
};

export const useLocalization = (): LocalizationContextType => {
    const context = useContext(LocalizationContext);
    if (!context) {
        throw new Error('useLocalization must be used within a LocalizationProvider');
    }
    return context;
};
